import { ChevronLeft, ChevronRight } from 'lucide-react';

// Add `n` days to a YYYY-MM-DD string and return the new YYYY-MM-DD string.
// Implemented in local-time to avoid DST/timezone drift around midnight.
// Empty input → relative to today (so prev/next on empty filter still works).
function addDays(ymd, n) {
  const base = ymd && /^\d{4}-\d{2}-\d{2}$/.test(ymd) ? ymd : (() => {
    const t = new Date();
    const pad = (x) => String(x).padStart(2, '0');
    return `${t.getFullYear()}-${pad(t.getMonth() + 1)}-${pad(t.getDate())}`;
  })();
  const [y, m, d] = base.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + n);
  const pad = (x) => String(x).padStart(2, '0');
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
}

/**
 * DateStepper — date input flanked by prev/next arrows.
 * Used in admin „Heute" (Dashboard), Tischplan, Buchungen (with date range).
 *
 * Props:
 *  - value: current YYYY-MM-DD
 *  - onChange: (newYmd) => void  (called for both arrows and manual input)
 *  - testIdPrefix: optional test-id prefix → renders -prev, -input, -next
 *  - inputClassName: optional extra classes for the <input>
 *  - size: 'sm' | 'md' (default 'md') — controls button size
 */
export default function DateStepper({ value, onChange, testIdPrefix = 'date-stepper', inputClassName = '', size = 'md' }) {
  const btnBase =
    'shrink-0 flex items-center justify-center border border-[#E5E5E0] rounded bg-white ' +
    'hover:border-[#1C1C19] hover:bg-[#F9F9F7] active:scale-95 transition disabled:opacity-40';
  const btnSize = size === 'sm' ? 'w-8 h-8' : 'w-10 h-10';
  const iconSize = size === 'sm' ? 'w-4 h-4' : 'w-5 h-5';
  return (
    <div className="inline-flex items-center gap-1.5">
      <button
        type="button"
        aria-label="Tag davor"
        title="Tag davor"
        onClick={() => onChange(addDays(value, -1))}
        data-testid={`${testIdPrefix}-prev`}
        className={`${btnBase} ${btnSize}`}
      >
        <ChevronLeft className={iconSize} />
      </button>
      <input
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`${testIdPrefix}-input`}
        className={`px-3 py-2 border border-[#E5E5E0] rounded text-sm bg-white ${inputClassName}`}
      />
      <button
        type="button"
        aria-label="Tag danach"
        title="Tag danach"
        onClick={() => onChange(addDays(value, 1))}
        data-testid={`${testIdPrefix}-next`}
        className={`${btnBase} ${btnSize}`}
      >
        <ChevronRight className={iconSize} />
      </button>
    </div>
  );
}

export { addDays };
